import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import ProcessItemCard from "./ProcessItemCard";
import { useUserStore } from "../store/userStore";
import {
  associateProcessByEmail,
  desassociateProcessByEmail,
} from "../services/clientService";
import { toast } from "react-toastify";

interface CardViewProcessProps {
  list: any;
  loadMoreData: () => void;
  isLoading?: boolean;
  reloadData: () => void;
}

const ProcessPerClientCardView: React.FC<CardViewProcessProps> = (
  props: CardViewProcessProps
) => {
  const [loading, setLoading] = useState(false);
  const [processList, setProcessList] = useState<any>([]);
  const [favList, setFavList] = useState<any>([]);
  const currentUserEmail = useUserStore((state) => state.email);
  const containerRef = useRef<HTMLDivElement>(null);
  const token = useUserStore((state) => state.idToken);

  useEffect(() => {
    setProcessList(props.list);
  }, [props.list]);

  const associatedUsers = (process: any) => {
    if (!process?.analistas || !Array.isArray(process.analistas)) {
      return false;
    }

    return process?.analistas?.some(
      (email: string) =>
        email.trim().toLowerCase() === currentUserEmail.trim().toLowerCase()
    );
  };

  const handleScroll = () => {
    if (containerRef.current && !props.isLoading) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        props.loadMoreData();
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const { scrollHeight, clientHeight } = container;
      if (
        scrollHeight <= clientHeight &&
        props.list.length > 0 &&
        !props.isLoading
      ) {
        props.loadMoreData();
      }
    }
  }, [props.list, props.isLoading]);

  const handleAssociar = async (codigo: string, isCurrentlyLinked: boolean) => {
    const email = currentUserEmail;

    try {
      if (!isCurrentlyLinked) {
        await associateProcessByEmail(codigo, email, token);
        toast.success("Processo associado com sucesso!");
      } else {
        await desassociateProcessByEmail(codigo, email);
        toast.success("Processo desassociado com sucesso!");
      }

      props.reloadData();
    } catch (error: any) {
      console.error("Erro:", error.response?.data || error.message);
      toast.error("Erro ao atualizar associação.");
    }
  };

  return (
    <>
      {!props.isLoading && processList.length === 0 && (
        <Box textAlign="left" color="#666" fontSize="1rem">
          Nenhum processo encontrado.
        </Box>
      )}
      {processList.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "86%",
          }}
        >
          <Box
            ref={containerRef}
            onScroll={handleScroll}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              padding: {
                xs: 0,
                sm: 2,
                md: 2,
              },
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(270px, 1fr))"
                gap={2}
              >
                {processList.map((process: any) => {
                  const userEmails = Array.isArray(process?.analistas)
                    ? process.analistas
                    : [];
                  const isUserLinked = userEmails.includes(currentUserEmail);

                  return (
                    <ProcessItemCard
                      key={process.CD_PROCESSO}
                      process={process}
                      onClick={() =>
                        handleAssociar(process.CD_PROCESSO, isUserLinked)
                      }
                      hasAssociatedUsers
                      checked={isUserLinked}
                    />
                  );
                })}
              </Box>
            )}
            {props.isLoading && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "10px",
                }}
              >
                <Box display="flex" justifyContent="center" alignItems="center">
                  <CircularProgress
                    size={48}
                    thickness={4}
                    style={{ color: "#326AFD" }}
                  />
                </Box>
              </div>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ProcessPerClientCardView;
